import { Button, Col, Form, Input, Row } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { MachinePart, WashingMachinePart } from '@/types'
import { CreateMachinePartsRequest } from '@/types/apis/machineParts/CreateMachinePartRequest'

import { SpaceCustom } from '../../style'

type Props = {
  loading: boolean
  onSubmit: (formValues: CreateMachinePartsRequest) => void
  washingMachinePartData: WashingMachinePart[] | undefined
  machineParts: MachinePart[] | undefined
}

type Item = {
  totalTimeUsed: string
  totalLife: string
}

type FormValues = {
  [key: number]: Item
}

const AddMachinePartComponentForm = ({ loading, onSubmit, washingMachinePartData, machineParts }: Props) => {
  const { id } = useParams()
  const { t } = useTranslation()

  const [form] = Form.useForm<FormValues>()
  const [washingMachinePart, setWashingMachinePart] = useState(washingMachinePartData)

  const handleFinish = (values: FormValues) => {
    const newValues = Object.fromEntries(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(values).filter(([_, value]) => value.totalTimeUsed && value.totalLife)
    )

    const formattedValues: CreateMachinePartsRequest = {
      washingMachineId: Number(id),
      washingParts: Object.entries(newValues).map(([key, value]) => ({
        washingMachinePartId: Number(key),
        totalLife: Number(value.totalLife),
        totalTimeUsed: Number(value.totalTimeUsed),
      })),
    }

    onSubmit(formattedValues)
  }

  useEffect(() => {
    if (machineParts && machineParts.length > 0) {
      const machinePartIds = machineParts.map((mp) => mp.washingMachinePartId)
      setWashingMachinePart(washingMachinePartData?.filter((wm) => !machinePartIds.includes(wm.id)))
    } else {
      setWashingMachinePart(washingMachinePartData)
    }
  }, [machineParts, washingMachinePartData])

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={{ washingParts: [{}] }}>
      {washingMachinePart?.map(({ id, name }) => (
        <Row gutter={16} key={id} align="middle">
          <Col span={8}>
            <Form.Item label={t('components.pages.machineParts.create.form.name')}>
              <Input disabled value={name} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={t('components.pages.machineParts.create.form.totalTimeUsed')}
              name={[id, 'totalTimeUsed']}
            >
              <Input addonAfter={t('components.pages.machineParts.create.form.timeUnit')} type="number" min={0} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={t('components.pages.machineParts.create.form.totalLife')} name={[id, 'totalLife']}>
              <Input addonAfter={t('components.pages.machineParts.create.form.timeUnit')} type="number" min={0} />
            </Form.Item>
          </Col>
        </Row>
      ))}

      {washingMachinePart && washingMachinePart.length > 0 && (
        <SpaceCustom>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t('components.pages.machineParts.create.form.submit')}
            </Button>
          </Form.Item>
        </SpaceCustom>
      )}
    </Form>
  )
}

AddMachinePartComponentForm.displayName = 'AddMachinePartComponentForm'
export default AddMachinePartComponentForm
